<style>
    @keyframes shakeup {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(-2px);
        }
        50% {
            transform: translateY(-4px);
        }
        75% {
            transform: translateY(-6px);
        }
        100% {
            transform: translateY(-8px);
        }
    }
    .shake-up {
        animation: shakeup 0.2s;
    }

    @keyframes shakedown {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(2px);
        }
        50% {
            transform: translateY(4px);
        }
        75% {
            transform: translateY(6px);
        }
        100% {
            transform: translateY(8px);
        }
    }
    .shake-down {
        animation: shakedown 0.2s;
    }

    .fade-out {
        display: none;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            visibility: hidden;
        }
        to {
            opacity: 1;
            visibility: visible;
        }
    }
    .fade-in {
        visibility: hidden; /* 初期状態を非表示に設定 */
        animation: fadeIn 0.2s forwards; /* アニメーション終了時にプロパティ値を保持 */
    }
</style>

<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import MultiFieldProInput from './MultiFieldProInput.svelte';
    import MultiFieldProAsset from './MultiFieldProAsset.svelte';
    import MultiFieldProContentData from './MultiFieldProContentData.svelte';
    import MultiFieldProTable from './MultiFieldProTable.svelte';
    import MultiFieldProTableVertical from './MultiFieldProTableVertical.svelte';
    import MultiFieldProChunk from './MultiFieldProChunk.svelte';
    import { createNewItem } from '../multiFieldPro/createNewItem.js';
    import { isPlainObject } from '../methods/isPlainObject.js';
    import { modalConfirm } from '../methods/modalConfirm.js';
    import { randomString } from '../methods/randomString.js';

    // ----------------------------------------
    // props で受け取る変数
    // ----------------------------------------
    export let mtappVars;
    export let id;
    export let storage;
    export let storageKey;
    export let handle;
    export let fieldBlocks;

    // ----------------------------------------
    // 親(MultiFieldPro)コンポーネント更新用イベント
    // ----------------------------------------
    const dispatch = createEventDispatcher();
    const triggerInputChange = () => {
        dispatch('inputchange');
    };

    // ----------------------------------------
    // ローカル変数
    // ----------------------------------------
    const staticPath = mtappVars.static_path;
    if (isPlainObject(fieldBlocks[handle].innerBlocks)) {
        fieldBlocks = { ...fieldBlocks, ...fieldBlocks[handle].innerBlocks };
    }

    // ----------------------------------------
    // リアクティブな変数
    // ----------------------------------------
    $: setClass = () => {
        if (typeof fieldBlocks[handle].addClass === 'string') {
            return fieldBlocks[handle].addClass.trim();
        } else if (typeof fieldBlocks[handle].addClass !== 'undefined' && Array.isArray(fieldBlocks[handle].addClass)) {
            return fieldBlocks[handle].addClass.join(' ');
        }
        return '';
    };

    /**
     * グループのクラスをセットします。
     *
     * @param {object} group
     */
    $: setGroupClass = (group) => {
        const className = ['position-relative'];
        if (group.hasBorder) {
            className.push('mf-loop-block');
        } else {
            className.push('my-5');
        }
        if (typeof group.addGroupClass !== 'undefined') {
            if (Array.isArray(group.addGroupClass) && group.addGroupClass.length > 0) {
                className.push(...group.addGroupClass);
            } else if (typeof group.addGroupClass === 'string' && group.addGroupClass !== '') {
                className.push(group.addGroupClass.trim());
            }
        }
        if (typeof group.condition !== 'undefined' && Array.isArray(group.condition)) {
            let match = 0;
            for (const cond of group.condition) {
                if (!Array.isArray(storage[storageKey][cond.groupHandle])) {
                    continue;
                }
                for (const fieldInGroup of storage[storageKey][cond.groupHandle]) {
                    if (
                        fieldInGroup.handle !== cond.fieldHandle ||
                        !['checkbox', 'radio', 'select'].includes(fieldBlocks[fieldInGroup.handle].type)
                    ) {
                        continue;
                    }
                    if (Object.prototype.hasOwnProperty.call(fieldInGroup, 'data') && cond.fieldValue === fieldInGroup.data) {
                        match++;
                    } else if (
                        Object.prototype.hasOwnProperty.call(fieldInGroup, fieldInGroup.handle) &&
                        cond.fieldValue === fieldInGroup[fieldInGroup.handle]
                    ) {
                        match++;
                    }
                }
            }
            if (match === group.condition.length) {
                className.push('fade-in');
            } else {
                className.push('fade-out');
            }
        }
        return className.join(' ');
    };

    // ----------------------------------------
    // メソッド
    // ----------------------------------------
    /**
     * フィールドを追加します。
     *
     * @param {string} groupHandle
     * @param {string} fieldHandle
     */
    function add(groupHandle, fieldHandle) {
        const newData = createNewItem(fieldBlocks, fieldHandle, ['expanded']);
        storage[storageKey][groupHandle].push(newData);
        storage[storageKey] = storage[storageKey];
        triggerInputChange();
    }

    /**
     * グループごとのボタンにクラスを付けます。
     *
     * @param {object} group
     */
    function setGroupButtonClass(group) {
        let className = ['btn-group', 'd-block'];
        let parentClass = [];
        if (Array.isArray(fieldBlocks[handle].addClass) && fieldBlocks[handle].addClass.length > 0) {
            parentClass.push(...fieldBlocks[handle].addClass);
        } else if (typeof fieldBlocks[handle].addClass === 'string' && fieldBlocks[handle].addClass !== '') {
            parentClass.push(...fieldBlocks[handle].addClass.split(' '));
        }
        if (Array.isArray(group.addGroupClass) && group.addGroupClass.length > 0) {
            parentClass.push(...group.addGroupClass);
        } else if (typeof group.addGroupClass === 'string' && group.addGroupClass !== '') {
            parentClass.push(...group.addGroupClass.split(' '));
        }
        if (parentClass.includes('row')) {
            className.push('col-12');
        }
        return className.join(' ');
    }

    /**
     * グループ内の各フィールドのクラスをセットします。
     *
     * @param {object} group
     */
    function setFieldGroupClass(group) {
        const className = ['my-5'];
        if (Array.isArray(group.addFieldClass) && group.addFieldClass.length > 0) {
            className.push(...group.addFieldClass);
        } else if (typeof group.addFieldClass === 'string' && group.addFieldClass !== '') {
            className.push(group.addFieldClass.trim());
        }
        return className.join(' ');
    }

    /**
     * グループ内のフィールドを上に移動します。
     *
     * @param {string} groupHandle
     * @param {number} i
     */
    function upInGroup(groupHandle, i) {
        storage[storageKey][groupHandle][i - 1].shakeDown = true;
        storage[storageKey][groupHandle][i].shakeUp = true;
        storage[storageKey] = storage[storageKey];
        setTimeout(() => {
            storage[storageKey][groupHandle].splice(i - 1, 2, storage[storageKey][groupHandle][i], storage[storageKey][groupHandle][i - 1]);
            delete storage[storageKey][groupHandle][i - 1].shakeUp;
            delete storage[storageKey][groupHandle][i].shakeDown;
            storage[storageKey] = storage[storageKey];
            triggerInputChange();
        }, 200);
    }

    /**
     * グループ内のフィールドを下に移動します。
     *
     * @param {string} groupHandle
     * @param {number} i
     */
    function downInGroup(groupHandle, i) {
        storage[storageKey][groupHandle][i].shakeDown = true;
        storage[storageKey][groupHandle][i + 1].shakeUp = true;
        storage[storageKey] = storage[storageKey];
        setTimeout(() => {
            storage[storageKey][groupHandle].splice(i, 2, storage[storageKey][groupHandle][i + 1], storage[storageKey][groupHandle][i]);
            delete storage[storageKey][groupHandle][i].shakeUp;
            delete storage[storageKey][groupHandle][i + 1].shakeDown;
            storage[storageKey] = storage[storageKey];
            triggerInputChange();
        }, 200);
    }

    /**
     * グループ内のフィールドを複製します。
     *
     * @param {string} groupHandle
     * @param {number} i
     */
    function duplicateInGroup(groupHandle, i) {
        const newItem = JSON.parse(JSON.stringify(storage[storageKey][groupHandle][i]));
        modalConfirm({
            type: 'info',
            title: '確認',
            content: `${fieldBlocks[newItem.handle].label}を複製しますか？`,
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                newItem.id = randomString();
                storage[storageKey][groupHandle].splice(i + 1, 0, newItem);
                storage[storageKey] = storage[storageKey];
                triggerInputChange();
            }
        });
    }

    /**
     * グループ内のフィールドを削除します。
     *
     * @param {string} groupHandle
     * @param {number} i
     */
    function removeInGroup(groupHandle, i) {
        modalConfirm({
            type: 'warning',
            title: '確認',
            content: '削除しますか？',
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                storage[storageKey][groupHandle].splice(i, 1);
                storage[storageKey] = storage[storageKey];
                triggerInputChange();
            }
        });
    }

    onMount(() => {
        fieldBlocks[handle].innerGroups.forEach((group) => {
            if (typeof storage[storageKey][group.handle] === 'undefined' || !Array.isArray(storage[storageKey][group.handle])) {
                storage[storageKey][group.handle] = [];
            }
            if (group.fixed) {
                const existingHandles = [];
                storage[storageKey][group.handle].forEach((item) => {
                    existingHandles.push(item.handle);
                });
                group.fields.forEach((fieldHandle, fieldIndex) => {
                    if (existingHandles.includes(fieldHandle)) {
                        return;
                    }
                    const newItem = createNewItem(fieldBlocks, fieldHandle, ['expanded']);
                    storage[storageKey][group.handle].splice(fieldIndex, 0, newItem);
                });
            }
        });
        storage[storageKey] = storage[storageKey];
        triggerInputChange();
    });
</script>

<div id={id} data-handle={handle} class={setClass()}>
    <!-- グループのループ -->
    {#each fieldBlocks[handle].innerGroups as group (group.handle)}
        <div class={setGroupClass(group)}>
            {#if group.title}
                <div class="mb-4">
                    <span class="badge badge-default position-relative">{group.title}</span>
                </div>
            {/if}
            {#if group.description}
                <div class="mb-4">{@html group.description}</div>
            {/if}

            <!-- グループ内のフィールドのループ -->
            {#if typeof storage[storageKey][group.handle] !== 'undefined' && Array.isArray(storage[storageKey][group.handle])}
                {#each storage[storageKey][group.handle] as savedItem, itemIndex (savedItem.id)}
                    <div
                        class={setFieldGroupClass(group)}
                        class:shake-up={typeof storage[storageKey][group.handle][itemIndex].shakeUp !== 'undefined' &&
                            storage[storageKey][group.handle][itemIndex].shakeUp}
                        class:shake-down={typeof storage[storageKey][group.handle][itemIndex].shakeDown !== 'undefined' &&
                            storage[storageKey][group.handle][itemIndex].shakeDown}>
                        <!-- フィールドのラベルと説明 -->
                        <div class="d-flex justify-content-between">
                            <div>
                                {#if fieldBlocks[savedItem.handle].label}
                                    <div class="mb-3 fw-bold">
                                        {fieldBlocks[savedItem.handle].label}
                                        {#if fieldBlocks[savedItem.handle].required}
                                            <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
                                        {/if}
                                    </div>
                                {/if}
                                {#if fieldBlocks[savedItem.handle].description}
                                    <p class="mb-3 mtapp-mf-description">{@html fieldBlocks[savedItem.handle].description}</p>
                                {/if}
                            </div>
                            <div class="pb-2">
                                {#if !group.fixed}
                                    <div class="btn-group btn-group-sm" role="group">
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            on:click={() => upInGroup(group.handle, itemIndex)}
                                            disabled={itemIndex === 0}
                                            aria-label="Move Up">
                                            <svg role="img" class="mt-icon--secondary mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-up'}></use>
                                            </svg>
                                        </button>
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            on:click={() => downInGroup(group.handle, itemIndex)}
                                            disabled={itemIndex === storage[storageKey][group.handle].length - 1}
                                            aria-label="Move Down">
                                            <svg role="img" class="mt-icon--secondary mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-down'}></use>
                                            </svg>
                                        </button>
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            on:click={() => duplicateInGroup(group.handle, itemIndex)}
                                            aria-label="Duplicate">
                                            <svg role="img" class="mt-icon--secondary mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_duplicate'}></use>
                                            </svg>
                                        </button>
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            on:click={() => removeInGroup(group.handle, itemIndex)}
                                            aria-label="Remove">
                                            <svg role="img" class="mt-icon--secondary mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_trash'}></use>
                                            </svg>
                                        </button>
                                    </div>
                                {/if}
                            </div>
                        </div>
                        <!-- END フィールドのラベルと説明 -->

                        {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(fieldBlocks[savedItem.handle].type)}
                            <MultiFieldProInput
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlock={fieldBlocks[savedItem.handle]}
                                on:inputchange={triggerInputChange} />
                        {:else if ['asset', 'image'].includes(fieldBlocks[savedItem.handle].type)}
                            <MultiFieldProAsset
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlock={fieldBlocks[savedItem.handle]}
                                on:inputchange={triggerInputChange} />
                        {:else if fieldBlocks[savedItem.handle].type === 'contentData'}
                            <MultiFieldProContentData
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlock={fieldBlocks[savedItem.handle]}
                                on:inputchange={triggerInputChange} />
                        {:else if fieldBlocks[savedItem.handle].type === 'table'}
                            <MultiFieldProTable
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlocks={fieldBlocks}
                                on:inputchange={triggerInputChange} />
                        {:else if ['table-vertical', 'table-vertical-fixed'].includes(fieldBlocks[savedItem.handle].type)}
                            <MultiFieldProTableVertical
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlocks={fieldBlocks}
                                on:inputchange={triggerInputChange} />
                        {:else if fieldBlocks[savedItem.handle].type === 'chunk'}
                            <MultiFieldProChunk
                                mtappVars={mtappVars}
                                id={savedItem.id}
                                storage={storage[storageKey][group.handle][itemIndex]}
                                storageKey={typeof storage[storageKey][group.handle][itemIndex][savedItem.handle] !== 'undefined'
                                    ? savedItem.handle
                                    : 'data'}
                                handle={savedItem.handle}
                                fieldBlocks={fieldBlocks}
                                on:inputchange={triggerInputChange} />
                        {/if}
                    </div>
                {/each}
            {/if}
            <!-- END グループ内のフィールドのループ -->

            {#if group.note || group.help}
                <div class="mb-3 text-muted">{@html group.note || group.help}</div>
            {/if}

            <!-- グループ内のフィールドを追加するボタン -->
            {#if !group.fixed}
                <div class={setGroupButtonClass(group)} role="group">
                    {#each group.fields as fieldHandle (fieldHandle)}
                        <button type="button" class="btn btn-default" on:click={() => add(group.handle, fieldHandle)}>
                            {fieldBlocks[fieldHandle].addButtonLabel || fieldBlocks[fieldHandle].label}
                        </button>
                    {/each}
                </div>
            {/if}
            <!-- END グループ内のフィールドを追加するボタン -->
        </div>
    {/each}
    <!-- END グループのループ -->
</div>
